// src/components/permissions/PermissionList.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchPermissions, deletePermission, clearPermissionError, clearPermissionSuccess } from '../../store/actions/permissionActions';
import Pagination from '../common/Pagination';
import SearchBar from '../common/SearchBar';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';

const PermissionList = () => {
  const dispatch = useDispatch();
  const { permissions, loading, error, success, meta } = useSelector(
    (state) => state.permissions
  );

  const [filters, setFilters] = useState({
    page: 1,
    limit: 50,
    search: '',
    module: '',
  });

  const [selectedPermission, setSelectedPermission] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedModule, setSelectedModule] = useState('all');
  
  // Use ref to track initial mount
  const initialMount = useRef(true);
  const filtersRef = useRef(filters);

  // Styles object
  const styles = {
    container: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
    },
    title: {
      fontSize: '1.875rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    createButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'background-color 0.2s',
    },
    createButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    
    // Filters
    filtersContainer: {
      display: 'flex',
      gap: '1rem',
      marginBottom: '1.5rem',
      flexWrap: 'wrap',
      alignItems: 'center',
    },
    searchContainer: {
      flex: 1,
      minWidth: '250px',
    },
    moduleFilter: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    moduleLabel: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#374151',
    },
    moduleSelect: {
      padding: '0.5rem 2rem 0.5rem 0.75rem',
      fontSize: '0.875rem',
      color: '#111827',
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      cursor: 'pointer',
      appearance: 'none',
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
      backgroundPosition: 'right 0.5rem center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '1.5em 1.5em',
    },
    moduleSelectFocus: {
      borderColor: '#2563eb',
      boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
    },
    
    // Stats
    statsContainer: {
      display: 'flex',
      gap: '1rem',
      marginBottom: '1rem',
      padding: '1rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
    },
    statItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    statLabel: {
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    statValue: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: '#111827',
    },
    
    // Table
    tableContainer: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      overflow: 'hidden',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    tableHeader: {
      backgroundColor: '#f9fafb',
      borderBottom: '1px solid #e5e7eb',
    },
    tableHeaderCell: {
      padding: '0.75rem 1.5rem',
      textAlign: 'left',
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#6b7280',
    },
    tableHeaderCellRight: {
      padding: '0.75rem 1.5rem',
      textAlign: 'right',
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#6b7280',
    },
    tableRow: {
      borderBottom: '1px solid #e5e7eb',
      transition: 'background-color 0.2s',
    },
    tableRowHover: {
      backgroundColor: '#f9fafb',
    },
    tableCell: {
      padding: '1rem 1.5rem',
      fontSize: '0.875rem',
      color: '#111827',
    },
    tableCellRight: {
      padding: '1rem 1.5rem',
      fontSize: '0.875rem',
      color: '#111827',
      textAlign: 'right',
    },
    
    // Permission badge
    permissionName: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontWeight: 500,
    },
    moduleBadge: {
      padding: '0.125rem 0.375rem',
      fontSize: '0.625rem',
      fontWeight: 500,
      backgroundColor: '#dbeafe',
      color: '#1e40af',
      borderRadius: '0.25rem',
      textTransform: 'uppercase',
    },
    actionBadge: {
      padding: '0.125rem 0.375rem',
      fontSize: '0.625rem',
      fontWeight: 500,
      backgroundColor: '#f3f4f6',
      color: '#4b5563',
      borderRadius: '0.25rem',
      marginLeft: '0.5rem',
    },
    
    // Description
    description: {
      maxWidth: '300px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      color: '#6b7280',
      fontSize: '0.875rem',
    },
    
    // Resource
    resourceTag: {
      padding: '0.125rem 0.375rem',
      fontSize: '0.625rem',
      fontWeight: 500,
      backgroundColor: '#e0f2fe',
      color: '#0369a1',
      borderRadius: '0.25rem',
    },
    
    // Actions
    actionsContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: '0.5rem',
    },
    actionButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.25rem',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      color: '#6b7280',
      transition: 'color 0.2s',
      borderRadius: '0.375rem',
    },
    viewButton: {
      color: '#2563eb',
    },
    viewButtonHover: {
      color: '#1d4ed8',
    },
    editButton: {
      color: '#4f46e5',
    },
    editButtonHover: {
      color: '#4338ca',
    },
    deleteButton: {
      color: '#dc2626',
    },
    deleteButtonHover: {
      color: '#b91c1c',
    },
    icon: {
      width: '1.25rem',
      height: '1.25rem',
    },
    
    // Empty state
    emptyState: {
      textAlign: 'center',
      padding: '3rem 1.5rem',
      color: '#6b7280',
      fontSize: '0.875rem',
    },
    emptyStateIcon: {
      width: '4rem',
      height: '4rem',
      margin: '0 auto 1rem',
      color: '#d1d5db',
    },
    
    // Loading overlay
    loadingOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
  };

  // Get unique modules for filter
  const modules = React.useMemo(() => {
    const uniqueModules = [...new Set(permissions.map(p => p.module))];
    return uniqueModules.sort();
  }, [permissions]);

  // Module statistics
  const moduleStats = React.useMemo(() => {
    const stats = {};
    permissions.forEach(p => {
      stats[p.module] = (stats[p.module] || 0) + 1;
    });
    return stats;
  }, [permissions]);

  // Memoize fetch function
  const fetchPermissionsData = useCallback(() => {
    dispatch(fetchPermissions(filters));
  }, [dispatch, filters]);

  // Only fetch when filters actually change
  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false;
      fetchPermissionsData();
    } else {
      if (JSON.stringify(filtersRef.current) !== JSON.stringify(filters)) {
        filtersRef.current = filters;
        fetchPermissionsData();
      }
    }
  }, [fetchPermissionsData, filters]);

  // Cleanup function
  useEffect(() => {
    return () => {
      dispatch(clearPermissionError());
      dispatch(clearPermissionSuccess());
    };
  }, [dispatch]);

  const handleSearch = useCallback((searchTerm) => {
    setFilters(prev => ({
      ...prev,
      search: searchTerm,
      page: 1,
    }));
  }, []);

  const handleModuleChange = useCallback((e) => {
    const module = e.target.value;
    setSelectedModule(module);
    setFilters(prev => ({
      ...prev,
      module: module === 'all' ? '' : module,
      page: 1,
    }));
  }, []);

  const handlePageChange = useCallback((page) => {
    setFilters(prev => ({
      ...prev,
      page,
    }));
  }, []);

  const handleDeleteClick = useCallback((permission) => {
    setSelectedPermission(permission);
    setShowDeleteConfirm(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      await dispatch(deletePermission(selectedPermission.id));
      setShowDeleteConfirm(false);
      setSelectedPermission(null);
      fetchPermissionsData();
    } catch (error) {
      console.error('Delete failed:', error);
    }
  }, [dispatch, selectedPermission, fetchPermissionsData]);

  const handleCloseDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setSelectedPermission(null);
  }, []);

  const handleClearError = useCallback(() => {
    dispatch(clearPermissionError());
  }, [dispatch]);

  const handleClearSuccess = useCallback(() => {
    dispatch(clearPermissionSuccess());
  }, [dispatch]);

  if (loading && !permissions.length) {
    return (
      <div style={styles.loadingOverlay}>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Gestion des permissions</h1>
        <Link
          to="/admin/permissions/form"
          style={styles.createButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = styles.createButtonHover.backgroundColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = styles.createButton.backgroundColor;
          }}
        >
          <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Créer une permission
        </Link>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={handleClearError}
        />
      )}

      {success && (
        <Alert
          type="success"
          message={success}
          onClose={handleClearSuccess}
        />
      )}

      {/* Filters */}
      <div style={styles.filtersContainer}>
        <div style={styles.searchContainer}>
          <SearchBar
            onSearch={handleSearch}
            placeholder="Rechercher des permissions par nom ou description..."
            fullWidth
          />
        </div>
        <div style={styles.moduleFilter}>
          <span style={styles.moduleLabel}>Module:</span>
          <select
            value={selectedModule}
            onChange={handleModuleChange}
            style={styles.moduleSelect}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = styles.moduleSelectFocus.borderColor;
              e.currentTarget.style.boxShadow = styles.moduleSelectFocus.boxShadow;
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#d1d5db';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <option value="all">Tous les modules</option>
            {modules.map(module => (
              <option key={module} value={module}>
                {module} ({moduleStats[module] || 0})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Statistics */}
      <div style={styles.statsContainer}>
        <div style={styles.statItem}>
          <span style={styles.statLabel}>Total des Permissions:</span>
          <span style={styles.statValue}>{meta.total || permissions.length}</span>
        </div>
        <div style={styles.statItem}>
          <span style={styles.statLabel}>Modules:</span>
          <span style={styles.statValue}>{modules.length}</span>
        </div>
      </div>

      {/* Table */}
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead style={styles.tableHeader}>
            <tr>
              <th style={styles.tableHeaderCell}>Permission</th>
              <th style={styles.tableHeaderCell}>Module</th>
              <th style={styles.tableHeaderCell}>Action</th>
              <th style={styles.tableHeaderCell}>Ressource</th>
              <th style={styles.tableHeaderCell}>Description</th>
              <th style={styles.tableHeaderCellRight}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {permissions.map((permission) => (
              <tr
                key={permission.id}
                style={styles.tableRow}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = styles.tableRowHover.backgroundColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <td style={styles.tableCell}>
                  <span style={styles.permissionName}>
                    {permission.name}
                  </span>
                </td>
                <td style={styles.tableCell}>
                  <span style={styles.moduleBadge}>{permission.module}</span>
                </td>
                <td style={styles.tableCell}>
                  <span style={styles.actionBadge}>{permission.action}</span>
                </td>
                <td style={styles.tableCell}>
                  {permission.resource ? (
                    <span style={styles.resourceTag}>{permission.resource}</span>
                  ) : (
                    <span style={{ color: '#9ca3af' }}>—</span>
                  )}
                </td>
                <td style={styles.tableCell}>
                  <div style={styles.description} title={permission.description}>
                    {permission.description || '—'}
                  </div>
                </td>
                <td style={styles.tableCellRight}>
                  <div style={styles.actionsContainer}>
                    <Link
                      to={`/admin/permissions/${permission.id}/details`}
                      style={{
                        ...styles.actionButton,
                        ...styles.viewButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.viewButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.viewButton.color;
                      }}
                      title="View Details"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </Link>

                    <Link
                      to={`/admin/permissions/${permission.id}/edit`}
                      style={{
                        ...styles.actionButton,
                        ...styles.editButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.editButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.editButton.color;
                      }}
                      title="Edit Permission"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </Link>

                    <button
                      onClick={() => handleDeleteClick(permission)}
                      style={{
                        ...styles.actionButton,
                        ...styles.deleteButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.deleteButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.deleteButton.color;
                      }}
                      title="Delete Permission"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {permissions.length === 0 && !loading && (
          <div style={styles.emptyState}>
            <svg style={styles.emptyStateIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <p>Aucune permission trouvée</p>
          </div>
        )}

        <Pagination
          currentPage={meta.page}
          totalPages={meta.totalPages}
          onPageChange={handlePageChange}
        />
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={handleCloseDelete}
        onConfirm={handleDeleteConfirm}
        title="Supprimer la permission"
        message={`Êtes-vous sûr de vouloir supprimer la permission "${selectedPermission?.name}" ? Cette action ne peut pas être annulée.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />
    </div>
  );
};

export default PermissionList;